import { Spacer } from "@hackthenorth/north";
import { TextArea, InlineError } from "north.js";
import React, { useState } from "react";
import styled from "styled-components";

import {
  FormFieldLabelRow,
  OptionalLabel,
  TagInput,
} from "src/shared/components";

import {
  EXPERIENCE_GOALS_MAX_CHARS,
  getCustomInterestOptions,
  INTEREST_OPTIONS,
  MAX_INTERESTS_COUNT,
} from "../constants";
import {
  captionTextStyles,
  fieldLabelStyles,
  subtitleTextStyles,
  textAreaStyles,
} from "../styles";
import { ProfileFormValues } from "../types";

export interface TagsFieldsProps {
  staging: ProfileFormValues;
  setStagingField: <K extends keyof ProfileFormValues>(
    field: K,
    value: ProfileFormValues[K]
  ) => void;
  showErrors: boolean;
}

const TagsFields: React.FC<TagsFieldsProps> = ({
  staging,
  setStagingField,
  showErrors,
}) => {
  const [adding, setAdding] = useState(false);
  const interests = staging.interests ?? [];
  const interestsError =
    showErrors && interests.length === 0
      ? "Please select at least one project interest."
      : undefined;

  return (
    <>
      <Heading>Your Interests</Heading>

      <QuestionWrapper>
        <FieldLabel>What are your project interests?</FieldLabel>
        <Spacer height={4} />
        <HelperText>
          Please select up to {MAX_INTERESTS_COUNT} project topics you&rsquo;re
          most interested in building!
        </HelperText>
        <Spacer height={12} />
        <TagInputWrapper>
          <TagInput
            multi
            max={MAX_INTERESTS_COUNT}
            options={INTEREST_OPTIONS}
            selectedOptions={interests}
            setSelectedOptions={(selected) =>
              setStagingField("interests", selected)
            }
            userOptions={getCustomInterestOptions(interests)}
            adding={adding}
            setAdding={setAdding}
            canAdd={!adding && interests.length < MAX_INTERESTS_COUNT}
            showRemoveOnSelected
            stableAddInput
          />
        </TagInputWrapper>
        {interestsError && <InlineError>{interestsError}</InlineError>}
      </QuestionWrapper>

      <QuestionWrapper>
        <FormFieldLabelRow>
          <FieldLabel>
            What are you looking to get out of your experience at Hack the
            North?
          </FieldLabel>
          <OptionalLabel>Optional</OptionalLabel>
        </FormFieldLabelRow>
        <Spacer height={12} />
        <TextAreaStyled
          placeholder="Share what you hope to get out of the weekend"
          rows={2}
          fullWidth
          maxLength={EXPERIENCE_GOALS_MAX_CHARS}
          value={staging.experience_goals ?? ""}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
            setStagingField("experience_goals", e.target.value)
          }
        />
        <CharCount>
          {(staging.experience_goals ?? "").length}/{EXPERIENCE_GOALS_MAX_CHARS}{" "}
          chars
        </CharCount>
      </QuestionWrapper>
    </>
  );
};

export default TagsFields;

const Heading = styled.h3`
  font-size: 20px;
  font-weight: 900;
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.heading};
  line-height: 1.5;
  color: ${({ theme }) => theme.globalConstants.color.brandSecondary};
  margin-bottom: 24px;
`;

const QuestionWrapper = styled.div`
  width: 100%;
  margin-bottom: 36px;
`;

const FieldLabel = styled.p`
  ${fieldLabelStyles}
  color: ${({ theme }) => theme.globalConstants.color.textBody};
  margin: 0;
`;

const HelperText = styled.p`
  ${subtitleTextStyles}
  color: ${({ theme }) => theme.globalConstants.color.textBody};
  margin: 0;
`;

/** Profile-form-only TagInput typography — do not change shared TagInput. */
const TagInputWrapper = styled.div`
  button,
  input {
    font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
    font-weight: 400;
    font-size: 16px;
    line-height: 150%;
    letter-spacing: 0;
  }
`;

const TextAreaStyled = styled(TextArea)`
  ${textAreaStyles}
`;

const CharCount = styled.p`
  ${captionTextStyles}
  margin: 4px 0 0;
  text-align: right;
  color: ${({ theme }) => theme.globalConstants.color.textSecondary};
`;
